import {
  createContext, useContext, useState, useRef, useMemo, useLayoutEffect, useEffect, cloneElement, Children,
} from 'react';
import { createPortal } from 'react-dom';

// A small pop-up menu for the Transport board's 3-dot strip. It opens right next to the 3 dots that were clicked and stays
// with them, at any page zoom.
//
// Why not the shared dropdown menu: the page is scaled with CSS zoom on laptops (utils/desktopZoomFix.js), and that menu
// works out its position in screen pixels but is drawn inside the zoomed page, so it landed away from the button. This menu
// MEASURES how the browser maps positions to the page before it places itself (it puts the menu at a test position, reads
// back where it really appeared, and corrects for the difference), so it lines up whatever the zoom. It is used in place of
// the shared menu with the same pieces (Menu, Trigger, Content, Item, Separator), so the board's menus read the same.

const MenuContext = createContext(null);

/** Where the menu goes, in the browser's own measuring units. `trigger` is the button's rectangle, `menu` the menu's size,
 *  `viewport` the screen's size. The menu's right edge lines up with the button's right edge, just below it — or above it
 *  when there is no room below — and is kept inside the screen. */
export const placeMenu = ({ trigger, menu, viewport, margin = 8, gap = 6 }) => {
  const maxLeft = Math.max(margin, viewport.width - menu.width - margin);
  const left = Math.min(Math.max(trigger.right - menu.width, margin), maxLeft);
  let top = trigger.bottom + gap;
  if (top + menu.height > viewport.height - margin) top = trigger.top - gap - menu.height;
  const maxTop = Math.max(margin, viewport.height - menu.height - margin);
  top = Math.min(Math.max(top, margin), maxTop);
  return { left, top };
};

const MENU_CSS = `
@keyframes c30-menu-in { from { opacity: 0; transform: translateY(-6px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
.c30-menu-panel { animation: c30-menu-in 0.14s ease-out; transform-origin: top right; }
`;

export function MouseMenu({ children }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const value = useMemo(() => ({ open, setOpen, triggerRef }), [open]);
  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

export function MouseMenuTrigger({ asChild = false, children }) {
  const { open, setOpen, triggerRef } = useContext(MenuContext);
  if (asChild) {
    const child = Children.only(children);
    return cloneElement(child, {
      ref: triggerRef,
      'aria-haspopup': 'menu',
      'aria-expanded': open,
      onClick: (e) => {
        if (child.props.onClick) child.props.onClick(e);
        setOpen(!open);
      },
    });
  }
  return (
    <button type="button" ref={triggerRef} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
      {children}
    </button>
  );
}

export function MouseMenuContent({ children, className = '', onClick }) {
  const { open, setOpen, triggerRef } = useContext(MenuContext);
  const menuRef = useRef(null);
  const probeRef = useRef(null);

  // Place the menu next to its button (see the note at the top of this file for how the zoom is handled).
  useLayoutEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const trigger = triggerRef.current;
    const probe = probeRef.current;
    if (!menu || !trigger || !probe) return;
    menu.style.left = '100px';
    menu.style.top = '100px';
    const test = menu.getBoundingClientRect();
    const scaleX = test.left / 100 || 1; // how far the browser's measuring units are from the page's own pixels
    const scaleY = test.top / 100 || 1;
    const screen = probe.getBoundingClientRect(); // the bottom-right corner of the screen, in the same units
    const spot = placeMenu({
      trigger: trigger.getBoundingClientRect(),
      menu: { width: test.width, height: test.height },
      viewport: { width: screen.right, height: screen.bottom },
    });
    menu.style.left = `${spot.left / scaleX}px`;
    menu.style.top = `${spot.top / scaleY}px`;
    menu.style.visibility = 'visible';
  }, [open, triggerRef]);

  // Close on a click anywhere else, Escape, a resize or a scroll.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onPointerDown = (e) => {
      const target = e.target;
      if (menuRef.current && menuRef.current.contains(target)) return;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open, setOpen, triggerRef]);

  if (!open) return null;
  return createPortal(
    <>
      <style>{MENU_CSS}</style>
      <div ref={probeRef} aria-hidden="true" style={{ position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, pointerEvents: 'none', visibility: 'hidden' }} />
      <div
        ref={menuRef}
        role="menu"
        data-testid="c30-mouse-menu"
        onClick={(e) => { e.stopPropagation(); if (onClick) onClick(e); }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{ position: 'fixed', left: 0, top: 0, visibility: 'hidden', zIndex: 10000 }}
      >
        <div
          className={`c30-menu-panel min-w-[210px] rounded-2xl border border-slate-200/80 bg-white/95 p-1.5 backdrop-blur-xl ${className}`}
          style={{ boxShadow: '0 22px 50px -12px rgba(15,23,42,0.45), 0 4px 14px rgba(15,23,42,0.12)' }}
        >
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
}

export function MouseMenuItem({ children, onClick, onSelect, className = '', disabled = false, ...rest }) {
  const { setOpen } = useContext(MenuContext);
  const danger = /text-red/.test(className);
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      {...rest}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(false);
        if (onClick) onClick(e);
        if (onSelect) onSelect(e);
      }}
      className={`flex w-full items-center rounded-xl px-3 py-2 text-left text-[13px] font-semibold transition-colors disabled:opacity-50 ${
        danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-700 hover:bg-orange-50 hover:text-orange-700'
      }`}
    >
      {children}
    </button>
  );
}

export function MouseMenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-slate-100" />;
}
